import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { LOGO_URL, BrandWordmark } from "../components/Logo";
import { Badge } from "../components/ui/badge";
import { useLang } from "../context/LangContext";
import {
  Apple,
  PlayCircle,
  CheckCircle,
  ExternalLink,
  MapPin,
  Bell,
  BarChart3,
  Trophy,
  Award,
  CheckCircle2,
  Map as MapIcon,
  FileDown,
} from "lucide-react";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/79a58a55-f289-4633-bd2b-0ab66186629a/images/e6c09d45001803607391a1f4a7c38b689bf2a66a9a9159ecec73c565a73e58b7.png";
const APP_MOCKUPS =
  "https://static.prod-images.emergentagent.com/jobs/79a58a55-f289-4633-bd2b-0ab66186629a/images/32efc8a0756fc275db266e722303598728229673c7f7e4d7f56c731538c6f2da.png";

const APP_STORE_URL = "#";
const GOOGLE_PLAY_URL = "#";
const OFFICIAL_URL = "https://holylandaward.iarc.org/";

export default function LandingPage() {
  const { t, lang } = useLang();

  const features = [
    {
      icon: MapPin,
      title: t("features.gps_title"),
      text: t("features.gps_text"),
    },
    {
      icon: MapIcon,
      title: t("features.progress_title"),
      text: t("features.progress_text"),
    },
    {
      icon: Bell,
      title: t("features.chat_title"),
      text: t("features.chat_text"),
    },
    {
      icon: Trophy,
      title: t("features.route_title"),
      text: t("features.route_text"),
    },
    {
      icon: FileDown,
      title: t("features.adif_title"),
      text: t("features.adif_text"),
    },
  ];

  return (
    <div className="min-h-screen bg-[#13131A] text-white">
      <Navbar />

      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative pt-32 pb-24 px-6 overflow-hidden"
      >
        <div
          className="absolute inset-0 -z-10 opacity-30"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#13131A]/40 via-[#13131A]/85 to-[#13131A]" />
        <div className="absolute inset-0 -z-10 bg-grid-blue opacity-40" />

        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-12 items-center">
            <div className="flex-1 animate-fade-up">
              <a
                href={OFFICIAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-official-link"
                className="inline-flex items-center gap-2 text-[#0A84FF] hover:text-[#5AC8FA] text-lg md:text-xl font-semibold group mb-8"
              >
                <Award className="h-6 w-6" />
                <span>{t("hero.official_link")}</span>
                <ExternalLink className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
              </a>

              <div className="flex items-center gap-6 mb-8">
                <div className="relative shrink-0 animate-float">
                  <div className="absolute inset-0 rounded-[32px] bg-[#0A84FF] blur-3xl opacity-30" />
                  <img
                    src={LOGO_URL}
                    alt="HolyLand Award"
                    className="relative h-24 w-24 md:h-32 md:w-32 object-cover rounded-[26px] md:rounded-[32px]"
                    style={{ filter: "brightness(1.15) saturate(1.05)" }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h1
                    data-testid="hero-title"
                    className="font-display font-semibold tracking-tight text-white leading-[0.95]"
                    style={{ fontSize: "clamp(2.5rem, 7vw, 5.5rem)" }}
                  >
                    HolyLand
                    <br />
                    <span className="brand-text">Award</span>
                  </h1>
                </div>
              </div>

              {/* Subtitle */}
              <p
                data-testid="hero-subtitle"
                className="text-base md:text-lg text-white/85 font-medium leading-relaxed max-w-2xl mb-4"
              >
                {t("hero.subtitle")}
              </p>

              {/* Long description */}
              <p className="text-sm md:text-base text-white/60 leading-relaxed max-w-2xl mb-8">
                {t("hero.tagline")}
              </p>

              {/* PRIMARY CTA */}
              <div
                className="flex flex-wrap items-center gap-3 mb-8"
                data-testid="hero-store-buttons"
              >
                <a
                  href={APP_STORE_URL}
                  data-testid="hero-appstore-btn"
                  className="group flex items-center gap-4 bg-black hover:bg-zinc-900 transition-all hover:-translate-y-0.5 rounded-2xl px-6 py-3.5 border border-white/10"
                >
                  <Apple className="h-9 w-9" />
                  <div className="text-start">
                    <p className="text-[10px] text-white/55 uppercase tracking-wider">
                      {t("hero.appstore_kicker")}
                    </p>
                    <p className="text-lg font-semibold leading-tight">
                      App Store
                    </p>
                  </div>
                </a>
                <a
                  href={GOOGLE_PLAY_URL}
                  data-testid="hero-googleplay-btn"
                  className="group flex items-center gap-4 bg-black hover:bg-zinc-900 transition-all hover:-translate-y-0.5 rounded-2xl px-6 py-3.5 border border-white/10"
                >
                  <PlayCircle className="h-9 w-9 text-[#34C759]" />
                  <div className="text-start">
                    <p className="text-[10px] text-white/55 uppercase tracking-wider">
                      {t("hero.googleplay_kicker")}
                    </p>
                    <p className="text-lg font-semibold leading-tight">
                      Google Play
                    </p>
                  </div>
                </a>
              </div>

              {/* Official badge */}
              <Badge
                data-testid="hero-badge"
                className="bg-[#0A84FF]/10 text-[#5AC8FA] border border-[#0A84FF]/30 hover:bg-[#0A84FF]/15 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 max-w-full whitespace-normal text-left leading-snug text-[11px] md:text-xs font-medium"
              >
                <CheckCircle className="h-3 w-3 shrink-0" />
                <span>{t("hero.badge")}</span>
              </Badge>
            </div>

            {/* Right: real app screenshots styled as phones */}
            <div className="flex-1 relative max-w-md w-full">
              <div className="absolute inset-0 bg-[#0A84FF]/20 blur-3xl rounded-full" />
              <div
                className="relative animate-float"
                style={{ perspective: "1600px" }}
              >
                <div
                  className="relative flex items-center justify-center"
                  style={{
                    transformStyle: "preserve-3d",
                    transform:
                      "rotateY(-20deg) rotateX(8deg)",
                  }}
                >
                  {/* Phone 1: Map (back, left) */}
                  <div className="relative w-[55%] -mr-8 z-10">
                    <div className="rounded-[28px] bg-zinc-900 p-1.5 border border-white/10 shadow-2xl shadow-black/70">
                      <div className="relative aspect-[9/19.5] rounded-[22px] overflow-hidden bg-black">
                        <img
                          src={`/screens/${lang}/map.jpg`}
                          alt="HolyLand Award — карта"
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute top-1.5 left-1/2 -translate-x-1/2 h-4 w-20 bg-black rounded-full" />
                      </div>
                    </div>
                  </div>
                  {/* Phone 2: Dashboard (front, right) */}
                  <div
                    className="relative w-[55%] -ml-8 mt-10 z-20"
                    style={{ transform: "translateZ(40px)" }}
                  >
                    <div className="rounded-[28px] bg-zinc-900 p-1.5 border border-white/10 shadow-2xl shadow-black/70">
                      <div className="relative aspect-[9/19.5] rounded-[22px] overflow-hidden bg-black">
                        <img
                          src={`/screens/${lang}/dashboard.jpg`}
                          alt="HolyLand Award — прогресс"
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute top-1.5 left-1/2 -translate-x-1/2 h-4 w-20 bg-black rounded-full" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                data-testid="hero-stats-card"
                className="absolute -bottom-4 -left-4 ios-card-elevated p-4 max-w-[210px] hidden sm:block"
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-[#FF6B35]/20 flex items-center justify-center">
                    <MapPin className="h-5 w-5 text-[#FF6B35]" />
                  </div>
                  <div>
                    <p className="text-[10px] text-white/50 uppercase tracking-wider font-semibold">
                      {t("hero.stat_squares_label")}
                    </p>
                    <p className="text-sm font-semibold">
                      {t("hero.stat_squares_value")}
                    </p>
                  </div>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 ios-card-elevated p-4 hidden sm:block">
                <div className="flex items-center gap-2">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0A84FF] opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-[#0A84FF]"></span>
                  </span>
                  <span className="text-xs">{t("hero.stat_live")}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section
        id="features"
        data-testid="features-section"
        className="relative px-6 py-16"
      >
        <div className="max-w-7xl mx-auto">
          <div className="mb-10 max-w-2xl">
            <p className="text-[#0A84FF] text-xs font-semibold tracking-widest uppercase mb-3">
              {t("features.label")}
            </p>
            <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight text-white leading-[1.05]">
              {t("features.title")}
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((f, i) => (
              <div
                key={i}
                data-testid={`feature-card-${i}`}
                className="ios-card p-6 transition-all hover:-translate-y-1 hover:border-white/15"
              >
                <div className="inline-flex h-11 w-11 rounded-2xl bg-[#0A84FF]/15 border border-[#0A84FF]/30 items-center justify-center mb-4">
                  <f.icon className="h-5 w-5 text-[#0A84FF]" />
                </div>
                <h3 className="font-display font-semibold text-lg mb-2 text-white">
                  {f.title}
                </h3>
                <p className="text-white/55 leading-relaxed text-sm">
                  {f.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SCREENSHOTS GALLERY */}
      <section
        id="screens"
        data-testid="gallery-section"
        className="relative px-6 py-16"
      >
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 text-center">
            <p className="text-[#0A84FF] text-xs font-semibold tracking-widest uppercase mb-3">
              {t("gallery.label")}
            </p>
            <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight text-white leading-[1.05]">
              {t("gallery.title")}
            </h2>
          </div>

          <div
            className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6"
            data-testid="screens-grid"
          >
            {[
              { k: "map", color: "#FF6B35" },
              { k: "dashboard", color: "#0A84FF" },
              { k: "chat", color: "#FF453A" },
              { k: "route", color: "#34C759" },
              { k: "profile", color: "#5856D6" },
            ].map((s, i) => (
              <div
                key={s.k}
                data-testid={`screen-${s.k}`}
                className="flex flex-col items-center group"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <div
                  className="relative w-full max-w-[200px] aspect-[9/19] rounded-[28px] p-[6px] bg-gradient-to-br from-zinc-700 via-zinc-900 to-zinc-800 transition-all group-hover:-translate-y-2 group-hover:scale-[1.02]"
                  style={{
                    boxShadow: `0 20px 50px -20px ${s.color}40, 0 12px 28px rgba(0,0,0,0.55)`,
                  }}
                >
                  {/* Notch */}
                  <div className="absolute top-[10px] left-1/2 -translate-x-1/2 w-16 h-[18px] bg-black rounded-full z-10" />
                  <div className="w-full h-full rounded-[22px] overflow-hidden bg-[#13131A]">
                    <img
                      key={lang}
                      src={`/screens/${lang}/${s.k}.jpg?v=3`}
                      alt={t(`gallery.${s.k}_t`)}
                      className="w-full h-full object-cover object-top"
                      loading="lazy"
                    />
                  </div>
                  {/* Subtle reflective edge */}
                  <div className="pointer-events-none absolute inset-0 rounded-[28px] ring-1 ring-white/10" />
                </div>
                <p className="mt-5 font-display font-semibold text-base text-white">
                  {t(`gallery.${s.k}_t`)}
                </p>
                <p className="mt-1 text-xs text-white/50 text-center max-w-[200px] leading-relaxed">
                  {t(`gallery.${s.k}_d`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OFFICIAL LINK CARD (API card removed - app is paid) */}
      <section
        id="links"
        data-testid="links-section"
        className="relative px-6 py-16"
      >
        <div className="max-w-3xl mx-auto">
          <div className="mb-10 text-center">
            <p className="text-[#0A84FF] text-xs font-semibold tracking-widest uppercase mb-3">
              {t("links.label")}
            </p>
            <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight text-white leading-[1.05]">
              {t("links.title")}
            </h2>
          </div>

          <a
            href={OFFICIAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="link-official"
            className="block ios-card p-7 group transition-all hover:-translate-y-1 hover:border-[#FF6B35]/40"
          >
            <div className="flex items-start gap-4 mb-4">
              <div className="h-12 w-12 rounded-2xl bg-[#FF6B35]/15 border border-[#FF6B35]/30 flex items-center justify-center shrink-0">
                <Award className="h-5 w-5 text-[#FF6B35]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-white/50 uppercase tracking-wider font-semibold mb-1">
                  {t("links.official_kicker")}
                </p>
                <h3 className="font-display font-semibold text-lg text-white truncate">
                  {t("links.official_title")}
                </h3>
              </div>
              <ExternalLink className="h-4 w-4 text-white/30 group-hover:text-white/70 transition-colors" />
            </div>
            <p className="text-white/60 text-sm mb-3 leading-relaxed">
              {t("links.official_text")}
            </p>
            <p className="text-[#0A84FF] text-sm font-mono break-all">
              holylandaward.iarc.org
            </p>
          </a>
        </div>
      </section>

      {/* DOWNLOAD - final CTA */}
      <section
        id="download"
        data-testid="download-section"
        className="relative px-6 py-24"
      >
        <div className="max-w-5xl mx-auto">
          <div className="relative ios-card-elevated p-12 md:p-16 overflow-hidden text-center">
            <div className="absolute -top-32 -right-32 h-96 w-96 bg-[#0A84FF]/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-32 -left-32 h-96 w-96 bg-[#FF6B35]/15 rounded-full blur-3xl" />

            <div className="relative">
              <p className="text-[#0A84FF] text-xs font-semibold tracking-widest uppercase mb-3">
                {t("download.label")}
              </p>
              <h2 className="font-display font-semibold text-3xl md:text-5xl tracking-tight mb-5 leading-[1.05]">
                {t("download.title_prefix")}{" "}
                <BrandWordmark className="font-semibold" />
              </h2>
              <p className="text-white/55 text-base md:text-lg max-w-2xl mx-auto mb-8">
                {t("download.subtitle")}
              </p>

              <div
                className="flex flex-wrap items-center justify-center gap-3"
                data-testid="download-store-buttons"
              >
                <a
                  href={APP_STORE_URL}
                  data-testid="download-appstore-btn"
                  className="group flex items-center gap-4 bg-black hover:bg-zinc-900 transition-all hover:-translate-y-0.5 rounded-2xl px-6 py-4 border border-white/10"
                >
                  <Apple className="h-9 w-9" />
                  <div className="text-start">
                    <p className="text-[10px] text-white/55 uppercase tracking-wider">
                      {t("hero.appstore_kicker")}
                    </p>
                    <p className="text-lg font-semibold leading-tight">
                      App Store
                    </p>
                  </div>
                </a>
                <a
                  href={GOOGLE_PLAY_URL}
                  data-testid="download-googleplay-btn"
                  className="group flex items-center gap-4 bg-black hover:bg-zinc-900 transition-all hover:-translate-y-0.5 rounded-2xl px-6 py-4 border border-white/10"
                >
                  <PlayCircle className="h-9 w-9 text-[#34C759]" />
                  <div className="text-start">
                    <p className="text-[10px] text-white/55 uppercase tracking-wider">
                      {t("hero.googleplay_kicker")}
                    </p>
                    <p className="text-lg font-semibold leading-tight">
                      Google Play
                    </p>
                  </div>
                </a>
              </div>

              <div className="mt-10 flex items-center justify-center gap-6 text-xs text-white/45 flex-wrap">
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-[#34C759]" />
                  {t("download.tag_quality")}
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-[#34C759]" />
                  {t("download.tag_no_ads")}
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-[#34C759]" />
                  {t("download.tag_official")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
